import { useState, useEffect, useCallback } from 'react';
import { Alert } from 'react-native';
import { Subscription } from '../../shared/types';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateStripeId } from '../../shared/utils/validation';

interface UseSubscriptionOptions {
  customerId?: string;
  subscriptionId?: string;
  autoFetch?: boolean;
  onError?: (error: string) => void;
  showErrorAlert?: boolean;
}

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  subscriptions: Subscription[];
  loading: boolean;
  error: string | null;
  // Backward compatibility getters
  isLoading: boolean; // Maps to loading
  isError: boolean; // Maps to !!error
  refetch: () => Promise<void>;
  cancel: (subscriptionId?: string) => Promise<boolean>;
  reactivate: (subscriptionId?: string) => Promise<boolean>;
  updatePaymentMethod: (
    subscriptionId: string,
    paymentMethodId: string
  ) => Promise<boolean>;
}

export const useSubscription = (
  options: UseSubscriptionOptions = {}
): UseSubscriptionReturn => {
  const {
    customerId,
    subscriptionId,
    autoFetch = true,
    onError,
    showErrorAlert = true,
  } = options;

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage);
      onError?.(errorMessage);

      if (showErrorAlert) {
        Alert.alert('Subscription Error', errorMessage);
      }

      console.error('Subscription error:', errorMessage);
    },
    [onError, showErrorAlert]
  );

  const fetchSubscription = useCallback(
    async (id: string) => {
      if (!validateStripeId(id, 'subscription')) {
        handleError('Invalid subscription ID');
        return null;
      }

      try {
        const response = await fetch(`/api/payments/subscription/${id}`);

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(
            errorData.error || ERROR_MESSAGES.SUBSCRIPTION_NOT_FOUND
          );
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : ERROR_MESSAGES.SUBSCRIPTION_NOT_FOUND;
        handleError(errorMessage);
        return null;
      }
    },
    [handleError]
  );

  const fetchCustomerSubscriptions = useCallback(
    async (id: string) => {
      if (!validateStripeId(id, 'customer')) {
        handleError('Invalid customer ID');
        return [];
      }

      try {
        const response = await fetch(
          `/api/payments/customer/${id}/subscriptions`
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || ERROR_MESSAGES.CUSTOMER_NOT_FOUND);
        }

        const data = await response.json();
        return data.subscriptions || [];
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : ERROR_MESSAGES.CUSTOMER_NOT_FOUND;
        handleError(errorMessage);
        return [];
      }
    },
    [handleError]
  );

  const refetch = useCallback(async () => {
    if (!customerId && !subscriptionId) {
      handleError('Either customerId or subscriptionId is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (subscriptionId) {
        const sub = await fetchSubscription(subscriptionId);
        setSubscription(sub);
      }

      if (customerId) {
        const subs = await fetchCustomerSubscriptions(customerId);
        setSubscriptions(subs);

        // If no specific subscription ID, set the first active subscription
        if (!subscriptionId && subs.length > 0) {
          const activeSub =
            subs.find((s: Subscription) => s.status === 'active') || subs[0];
          setSubscription(activeSub);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [
    customerId,
    subscriptionId,
    fetchSubscription,
    fetchCustomerSubscriptions,
    handleError,
  ]);

  const cancel = useCallback(
    async (targetSubscriptionId?: string): Promise<boolean> => {
      const idToCancel = targetSubscriptionId || subscriptionId;

      if (!idToCancel) {
        handleError('Subscription ID is required to cancel');
        return false;
      }

      if (!validateStripeId(idToCancel, 'subscription')) {
        handleError('Invalid subscription ID');
        return false;
      }

      return new Promise(resolve => {
        Alert.alert(
          'Cancel Subscription',
          'Are you sure you want to cancel your subscription? It will remain active until the end of your current billing period.',
          [
            {
              text: 'Keep Subscription',
              style: 'cancel',
              onPress: () => resolve(false),
            },
            {
              text: 'Cancel Subscription',
              style: 'destructive',
              onPress: async () => {
                try {
                  const response = await fetch(
                    `/api/payments/subscription/${idToCancel}/cancel`,
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                    }
                  );

                  if (!response.ok) {
                    const errorData = await response
                      .json()
                      .catch(() => ({ error: 'Network error' }));
                    throw new Error(
                      errorData.error || 'Failed to cancel subscription'
                    );
                  }

                  await refetch(); // Refresh data
                  Alert.alert(
                    'Success',
                    'Your subscription has been cancelled and will end at the end of your current billing period.'
                  );
                  resolve(true);
                } catch (err) {
                  const errorMessage =
                    err instanceof Error
                      ? err.message
                      : 'Failed to cancel subscription';
                  handleError(errorMessage);
                  resolve(false);
                }
              },
            },
          ]
        );
      });
    },
    [subscriptionId, refetch, handleError]
  );

  const reactivate = useCallback(
    async (targetSubscriptionId?: string): Promise<boolean> => {
      const idToReactivate = targetSubscriptionId || subscriptionId;

      if (!idToReactivate) {
        handleError('Subscription ID is required to reactivate');
        return false;
      }

      if (!validateStripeId(idToReactivate, 'subscription')) {
        handleError('Invalid subscription ID');
        return false;
      }

      try {
        const response = await fetch(
          `/api/payments/subscription/${idToReactivate}/reactivate`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(
            errorData.error || 'Failed to reactivate subscription'
          );
        }

        await refetch(); // Refresh data
        Alert.alert('Success', 'Your subscription has been reactivated!');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to reactivate subscription';
        handleError(errorMessage);
        return false;
      }
    },
    [subscriptionId, refetch, handleError]
  );

  const updatePaymentMethod = useCallback(
    async (
      targetSubscriptionId: string,
      paymentMethodId: string
    ): Promise<boolean> => {
      if (!validateStripeId(targetSubscriptionId, 'subscription')) {
        handleError('Invalid subscription ID');
        return false;
      }

      try {
        const response = await fetch(
          `/api/payments/subscription/${targetSubscriptionId}/payment-method`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paymentMethodId }),
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || 'Failed to update payment method');
        }

        await refetch(); // Refresh data
        Alert.alert('Success', 'Payment method updated successfully!');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to update payment method';
        handleError(errorMessage);
        return false;
      }
    },
    [refetch, handleError]
  );

  useEffect(() => {
    if (autoFetch && (customerId || subscriptionId)) {
      refetch();
    }
  }, [autoFetch, customerId, subscriptionId, refetch]);

  return {
    subscription,
    subscriptions,
    loading,
    error,
    // Backward compatibility getters
    isLoading: loading,
    isError: !!error,
    refetch,
    cancel,
    reactivate,
    updatePaymentMethod,
  };
};
